import React from 'react';
import '../../css/legal/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy p-privacy">
      <div className="policy-container">
        <h1>Privacy Policy</h1>
        <p className="effective-date">Effective Date: 22 July 2025</p>
        
        <div className="policy-intro">
          <p className="p-privacy">Romulus Media, LLC ("Romulus Media", "we", "our", or "us") is committed to protecting your privacy and personal data. This Privacy Policy describes how we collect, use, disclose, and protect your information across our website (<a href="https://www.romulusmedia.com">https://www.romulusmedia.com</a>), digital services, creator programs, communications, and any other platform or engagement where this policy is referenced.</p>
          <p className="p-privacy">By accessing or using our services, you agree to the practices outlined in this Privacy Policy.</p>
        </div>

        <div className="policy-section">
          <h2>1. Information We Collect</h2>
          <p className="p-privacy">We collect both personal and non-personal information depending on how you interact with us. This may include:</p>
          
          <h3>A. Personal Information You Provide</h3>
          <ul>
            <li><b>• Contact Information:</b> name, phone number, email, mailing address, and company details</li>
            <li><b>• Business Details:</b> employer name, title, or client portfolio if representing a business</li>
            <li><b>• Creator Program Applications:</b> age, school, graduation year, social media handles, audience demographics, creative samples, and payment details</li>
            <li><b>• Payment and Transaction Data:</b> billing address, transaction history, and limited payment information (processed through third-party platforms)</li>
            <li><b>• Communications:</b> emails, forms, feedback, responses to surveys, or correspondence with our team</li>
            <li><b>• Account Credentials:</b> usernames, passwords, and profile details (if applicable)</li>
          </ul>
          
          <h3>B. Automatically Collected Information</h3>
          <p className="p-privacy">Through cookies, pixels, and tracking technologies:</p>
          <ul>
            <li>• IP address and device identifiers</li>
            <li>• Browser type and version</li>
            <li>• Operating system and device type</li>
            <li>• Website usage data (pages viewed, time spent, referring URLs, etc.)</li>
            <li>• Geolocation (approximate)</li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>2. How We Use Your Information</h2>
          <p className="p-privacy">We use collected data for:</p>
          <ul>
            <li>• Delivering and improving our website and services</li>
            <li>• Managing the Creator Program, client services, and content production</li>
            <li>• Communicating updates, announcements, and marketing materials</li>
            <li>• Processing payments, invoices, and contractual obligations</li>
            <li>• Understanding site usage and user behavior</li>
            <li>• Conducting business development and lead generation</li>
            <li>• Preventing fraud, misuse, or illegal activities</li>
            <li>• Complying with legal and contractual obligations</li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>3. Legal Bases for Processing (for EEA/UK Users)</h2>
          <p className="p-privacy">For users in the European Economic Area (EEA) or the United Kingdom, we process your personal data under one or more of the following legal bases:</p>
          <ul>
            <li><strong>Contractual Necessity:</strong> to provide services you request</li>
            <li><strong>Legitimate Interests:</strong> for business operations, marketing, fraud prevention, and platform improvements</li>
            <li><strong>Consent:</strong> where required (e.g., email marketing, cookies)</li>
            <li><strong>Legal Obligation:</strong> to comply with applicable laws and regulations</li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>4. Sharing and Disclosure of Information</h2>
          <p className="p-privacy">We do not sell your personal data. We only share it under the following circumstances:</p>
          <ul>
            <li><strong>• Service Providers and Vendors:</strong> such as hosting providers, cloud storage, marketing platforms, CRM tools (e.g. HubSpot), analytics (e.g. Google Analytics), and payment processors (e.g. Stripe, PayPal)</li>
            <li><strong>• Creator Program Partners:</strong> if you are a creator or brand participant, your data may be shared with involved parties only as necessary and with your consent</li>
            <li><strong>• Business Transfers:</strong> if Romulus Media is involved in a merger, acquisition, or sale of assets</li>
            <li><strong>• Legal Requirements:</strong> to comply with a subpoena, regulation, court order, or law enforcement request</li>
            <li><strong>• Protection of Rights:</strong> to enforce our agreements, protect users, or investigate fraud or security threats</li>
          </ul>
        </div>

        <div className="policy-section">
          <h2>5. Cookies and Tracking Technologies</h2>
          <p className="p-privacy">We use cookies, web beacons, and similar technologies to:</p>
          <ul>
            <li>• Enable website functionality</li>
            <li>• Analyze site usage and user preferences</li>
            <li>• Personalize content or marketing</li>
            <li>• Track conversions and performance of campaigns</li>
          </ul>
          <p className="p-privacy">You can control or disable cookies via your browser settings. Certain features may not work as intended if cookies are disabled.</p>
        </div>

        <div className="policy-section">
          <h2>6. Data Retention</h2>
          <p className="p-privacy">We retain personal data only as long as necessary for the purposes stated in this Privacy Policy or to comply with legal, contractual, or regulatory obligations.</p>
          <p className="p-privacy">When no longer required, we securely delete or anonymize your data.</p>
        </div>

        <div className="policy-section">
          <h2>7. Your Rights and Choices</h2>
          <p className="p-privacy">Depending on your location, you may have the following rights:</p>
          
          <h3>For California Residents (CCPA/CPRA):</h3>
          <ul>
            <li>• Know what personal data we collect</li>
            <li>• Request deletion of personal data</li>
            <li>• Opt-out of data "sales" or "sharing" (we do not sell personal data, but honor browser-based opt-outs)</li>
            <li>• Request correction of inaccurate data</li>
          </ul>
          <p className="p-privacy">To exercise rights, email: <a href="mailto:inquiries@romulusmedia.com">inquiries@romulusmedia.com</a></p>
          
          <h3>For EEA/UK Residents (GDPR):</h3>
          <ul>
            <li>• Access your data</li>
            <li>• Request rectification or erasure</li>
            <li>• Restrict or object to processing</li>
            <li>• Data portability</li>
            <li>• Withdraw consent at any time (without affecting prior processing)</li>
          </ul>
          <p className="p-privacy">Submit requests to: <a href="mailto:inquiries@romulusmedia.com">inquiries@romulusmedia.com</a></p>
          <p className="p-privacy">We may require verification before fulfilling requests.</p>
        </div>

        <div className="policy-section">
          <h2>8. Security</h2>
          <p className="p-privacy">We implement appropriate technical and organizational safeguards to protect your information, including encryption, access controls, and secure server environments. However, no digital system is entirely secure.</p>
        </div>

        <div className="policy-section">
          <h2>9. Children's Privacy</h2>
          <p className="p-privacy">Our services are not intended for individuals under the age of 13 (or 16 where applicable). We do not knowingly collect data from children. If we learn that we have unintentionally collected personal data from a child, we will delete it promptly.</p>
        </div>

        <div className="policy-section">
          <h2>10. Third-Party Links and Services</h2>
          <p className="p-privacy">Our website may contain links to third-party websites or platforms. We are not responsible for their content or privacy practices. Please review their privacy policies before providing any personal information.</p>
        </div>

        <div className="policy-section">
          <h2>11. International Transfers</h2>
          <p className="p-privacy">If you are located outside the United States, your information may be transferred to and processed in the U.S. or other countries. We use contractual safeguards (e.g. Standard Contractual Clauses) where applicable to ensure lawful transfers.</p>
        </div>

        <div className="policy-section">
          <h2>12. Changes to This Privacy Policy</h2>
          <p className="p-privacy">We may update this Privacy Policy from time to time. The "Effective Date" at the top reflects the latest version. We encourage you to review it regularly. Significant changes will be communicated clearly on our website or via email.</p>
        </div>

        <div className="policy-section">
          <h2>13. Contact Us</h2>
          <p className="p-privacy">For privacy-related inquiries, requests, or complaints, contact us at:</p>
          <p className="contact-info p-privacy">
            Romulus Media, LLC<br />
            Email: <a href="mailto:inquiries@romulusmedia.com">inquiries@romulusmedia.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;